// src/store/store.tsx
import { useDispatch as useReduxDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  createTransform,
} from 'redux-persist';
import createMigrate from 'redux-persist/lib/createMigrate.js';
import { configureStore, combineReducers, Action } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage/index.js'; // localStorage por defecto

/* ───────────────────────  Slices  ─────────────────────── */
import authReducer            from './slices/authSlice';
import userReducer            from './slices/userSlice';
import filterReducer          from './slices/filterSlice';
import settingsReducer        from './slices/settingsSlice';
import eshopReducer           from './slices/eshopSlice';
import cartReducer            from './slices/cartSlice';
import tourReducer            from './slices/tourSlice';
import uiReducer              from './slices/uiSlice';
import shippingMethodsReducer from './slices/shippingMethodsSlice';
import offersReducer          from './slices/offersSlice';
import saleOrdersReducer      from './slices/saleOrdersSlice';
import paymentsReducer        from './slices/paymentsSlice';
import posCustomersReducer    from './slices/posCustomersSlice';
import editorReducer          from '../features/cms/state/editorSlice.ts';
import templateDraftReducer   from '../features/eshop/templates/state/templateDraftSlice.ts';
import expensesReducer        from '../features/erp/expenses/state/expensesSlice.ts';
import chatsReducer           from './slices/chatsSlice.tsx';
import type { ResetAppAction } from './resetAction';


/* ────────────────────────────────
 * 1. Definimos migraciones
 * ────────────────────────────────*/
const migrations = {
  // v0 → v1: rompimos compatibilidad ⇒ purgamos todo
  1: () => undefined,

  // v1 → v2: purgar todo el state (por cambio de estructura en AlertsPanel, etc.)
  2: () => undefined,

  // // v2 → v3: purgar todo el state (por cambio de estructura en AlertsPanel, etc.)
  // 3: () => undefined,
};

/* ─────── 2· Transform específico para el slice auth ─────── */
const authTransform = createTransform(
  // Al persistir → quitamos la prop ``status``
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (inboundState: any) => {
    // Antes de guardar: eliminamos status
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { status, registration, ...rest } = inboundState;
    return rest;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (outboundState: any) => {
    // Al rehidratar: forzamos una comprobación de sesión
    return {
      ...outboundState,
      status: 'checking',
      registration: { status: 'idle', error: null },
    };
  },
  { whitelist: ['auth'] },
);

// Transform para el slice `eshop`: evita persistir flags de carga
const eshopTransform = createTransform(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (inboundState: any) => {
    // Antes de guardar: omitimos los flags transitorios
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isItemLoading, isCategoriesLoading, initialized, ...rest } = inboundState;
    return rest;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (outboundState: any) => ({
    // Al rehidratar: aseguramos estado limpio
    ...outboundState,
    isItemLoading: false,
    isCategoriesLoading: false,
    initialized: false,
  }),
  { whitelist: ['eshop'] },
);

/* ─────────── 3· Configuración de persistencia ─────────── */
const rootPersistConfig = {
  key: 'root',
  version: 5,                           // ⬆️ súbelo cuando cambies estructura
  migrate: createMigrate(migrations, { debug: false }),
  storage,
  whitelist: [
    'auth',
    'user',
    'filter',
    'stats',
    'settings',
    'sectors',
    'eshop',
    'ui',
    'shippingMethods',
    'offers',
    'saleOrders',
    'payments',
    'templateDrafts',
  ],        // Slices que persisten
  transforms: [authTransform, eshopTransform],
};

/* ──────────────── 4· Root reducer  ──────────────── */
const appReducer = combineReducers({
  auth:   authReducer,
  user:   userReducer,
  filter: filterReducer,
  settings: settingsReducer,
  eshop:  eshopReducer,
  cart:  cartReducer,
  tour:  tourReducer,
  ui:    uiReducer,
  shippingMethods: shippingMethodsReducer,
  offers: offersReducer,
  saleOrders: saleOrdersReducer,
  payments: paymentsReducer,
  posCustomers: posCustomersReducer,
  editor: editorReducer,
  templateDrafts: templateDraftReducer,
  expenses: expensesReducer,
  chats: chatsReducer,
});

export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: ResetAppAction | Action,
) => {
  if (action.type === 'APP/RESET') {
    state = undefined;          // ← resetea todo
  }
  return appReducer(state, action);
};


const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(rootPersistConfig, rootReducer);

/* ──────────────── 5· Store  ──────────────── */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        /* Ignora acciones internas de redux-persist */
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: import.meta.env.MODE !== 'production',
});

/* ──────────────── 6· Persistor  ──────────────── */
export const persistor = persistStore(store);

/* ──────────────── 7· Hooks tipados  ──────────────── */
export type RootState   = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useReduxDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/* ──────────────── 8· Helper de purge  ──────────────── */
export const purgePersistedState = async () => {
  try {
    await persistor.purge();
    window.location.reload();      // reinicia la app en limpio
  } catch (err) {
    console.error('Error purgando persist:', err);
  }
};

/* Exponemos el helper en modo DEV para depurar rápido */
if (import.meta.env.MODE !== 'production') {
  // @ts-expect-error – solo para facilitar pruebas en consola
  window.__PURGE_REDUX__ = purgePersistedState;
}
